import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { superadminAPI } from '../api';
import { ArrowLeft, Shield, Loader2, Building2 } from 'lucide-react';
import SettingsPage from './SettingsPage';

/**
 * SuperAdmin "Manage Tenant" wrapper.
 * Activates `X-Acting-Tenant-Id` header for the duration of this view (via sessionStorage),
 * then renders the standard SettingsPage which now operates on the impersonated tenant.
 */
export default function ManageTenantPage() {
  const { tenantId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  // CRITICAL: set the impersonation header SYNCHRONOUSLY during render (via useState lazy initializer)
  // so any child effect (e.g. SettingsPage → SSOTab.loadSSO) that fires on first commit
  // already sees the correct X-Acting-Tenant-Id header. Otherwise, child effects run BEFORE
  // the parent's useEffect — meaning loadSSO would query the SuperAdmin's own tenant (and return
  // empty), which felt like "save did nothing" to users.
  useState(() => {
    if (tenantId) sessionStorage.setItem('bkw_acting_tenant_id', tenantId);
    return null;
  });

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/dashboard');
      return undefined;
    }
    sessionStorage.setItem('bkw_acting_tenant_id', tenantId);
    superadminAPI.getTenantDetail(tenantId).then(r => {
      setTenant(r.data?.company || r.data || null);
    }).catch(() => {}).finally(() => setLoading(false));
    return () => { sessionStorage.removeItem('bkw_acting_tenant_id'); };
  }, [tenantId, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#6590FF]" />
      </div>
    );
  }

  return (
    <div data-testid="manage-tenant-page">
      <div className="bg-[#FEF3C7] border-b border-[#FDE68A] px-6 py-2.5 flex items-center gap-3 sticky top-0 z-40">
        <button onClick={() => navigate('/superadmin')} className="p-1.5 hover:bg-[#FDE68A] rounded-lg flex items-center gap-1.5 text-xs font-semibold text-[#78350F]" data-testid="manage-tenant-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to SuperAdmin
        </button>
        <div className="flex items-center gap-2 text-xs text-[#78350F]">
          <Shield className="w-3.5 h-3.5" />
          <span className="font-bold uppercase tracking-wider">Acting as Admin</span>
          <span className="opacity-60">·</span>
          <Building2 className="w-3.5 h-3.5" />
          <span className="font-semibold">{tenant?.name || tenantId}</span>
        </div>
      </div>
      <SettingsPage />
    </div>
  );
}
